import React, { useEffect, useState } from 'react';
import { getStaffTips, StaffTips as Summary } from '../../../../../crud/management_api/feature_rollout';
import {
  STAFF_TIPS_REFRESH_MS, isFeatureRolloutLocal, staffTipsMessages as copy,
} from '../../../../../configuration/directMenuRollout';
import Button from '../../../../../components/ui/Button/Button';

type State = { kind: 'hidden' | 'loading' | 'error' } | { kind: 'ready'; data: Summary };
const visible = () => document.visibilityState !== 'hidden';

/** Mount no polling loop for other restaurants, even if the browser has stale flags. */
export default function StaffTips({ localId, userId }: { localId: number; userId: number }) {
  if (!isFeatureRolloutLocal(localId)) return null;
  return <EligibleStaffTips key={`${localId}-${userId}`} localId={localId} />;
}

function EligibleStaffTips({ localId }: { localId: number }) {
  const [state, setState] = useState<State>({ kind: 'hidden' });
  const [retry, setRetry] = useState(0);
  useEffect(() => {
    let disposed = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let current: AbortController | undefined;
    let enabled = false;
    const load = async () => {
      if (disposed || !visible()) return;
      current?.abort();
      const controller = new AbortController();
      current = controller;
      if (enabled) setState({ kind: 'loading' });
      try {
        const data = await getStaffTips(localId, controller.signal);
        if (!disposed && !controller.signal.aborted) {
          enabled = data.state !== 'disabled';
          setState({ kind: 'ready', data });
        }
      } catch {
        if (!disposed && !controller.signal.aborted) setState({ kind: 'error' });
      } finally {
        if (!disposed && !controller.signal.aborted && visible()) {
          timer = setTimeout(load, STAFF_TIPS_REFRESH_MS);
        }
      }
    };
    const visibility = () => {
      clearTimeout(timer);
      current?.abort();
      if (document.visibilityState === 'hidden') setState({ kind: 'hidden' });
      else void load();
    };
    document.addEventListener('visibilitychange', visibility);
    void load();
    return () => {
      disposed = true;
      clearTimeout(timer);
      current?.abort();
      document.removeEventListener('visibilitychange', visibility);
    };
  }, [localId, retry]);

  if (state.kind === 'hidden' || (state.kind === 'ready' && state.data.state === 'disabled')) return null;
  return (
    <section aria-label={copy.title} className="max-w-40 text-xs leading-tight">
      {state.kind === 'loading' && <span role="status">{copy.loading}</span>}
      {state.kind === 'error' && <>
        <span role="alert">{copy.unavailable}</span>
        <Button size="sm" variant="ghost" onClick={() => setRetry(value => value + 1)}>{copy.retry}</Button>
      </>}
      {state.kind === 'ready' && state.data.state === 'no_session' && <span>{copy.noSession}</span>}
      {state.kind === 'ready' && state.data.state === 'active' && (
        <output aria-label={copy.title}>
          <span className="block">{copy.title}</span>
          {state.data.currency} {state.data.collected_tips}
        </output>
      )}
    </section>
  );
}
